import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { GarantieReferentielDto } from 'api/guarantee';
import { Sizes } from 'enums/sizes';
import { guaranteesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { GuaranteeReferentialModal } from './forms/GuaranteeReferentialModal';
import { Branch } from './useBranches';

interface UseGuaranteeActionsOptions {
  branches: Branch[];
  /** Branche présélectionnée à la création. */
  branchId?: string;
  onChanged: () => void | Promise<void>;
}

/**
 * Ouverture des modales et suppression d'une garantie du référentiel.
 *
 * Mutualisé entre l'écran d'une branche et celui des garanties orphelines : les deux
 * proposent exactement les mêmes actions, seul le contexte de création diffère.
 */
export const useGuaranteeActions = ({
  branches,
  branchId,
  onChanged,
}: UseGuaranteeActionsOptions) => {
  const { openBlankDialog } = useDialog();
  const [toDelete, setToDelete] = useState<GarantieReferentielDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openGuarantee = useCallback(
    (guarantee?: GarantieReferentielDto) =>
      openBlankDialog(
        { size: Sizes['3xl'] },
        <GuaranteeReferentialModal
          guarantee={guarantee}
          branchId={branchId}
          branches={branches}
          onSuccess={onChanged}
        />
      ),
    [openBlankDialog, branchId, branches, onChanged]
  );

  const confirmDelete = useCallback(async () => {
    if (!toDelete?.id) return;

    setIsDeleting(true);
    try {
      const response = await guaranteesAPI.deleteReferentialGuarantee({
        garantieId: toDelete.id,
      });
      if (response.isSuccess) {
        toast.success('Garantie supprimée');
        await onChanged();
      } else {
        // Le back refuse la suppression d'une garantie utilisée par un tunnel : son
        // message est plus précis que tout ce qu'on pourrait deviner ici.
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (error) {
      console.error('Suppression de la garantie impossible', error);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  }, [toDelete, onChanged]);

  return {
    openGuarantee,
    guaranteeToDelete: toDelete,
    requestDelete: setToDelete,
    cancelDelete: () => setToDelete(null),
    confirmDelete,
    isDeleting,
  };
};
